'use client';

import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { OtpChannel } from '@domain/models/otp';
import {
  getOtpSendStatus as getOtpSendStatusService,
  sendOtp as sendOtpService,
  verifyOtp as verifyOtpService
} from '@website/services/otp.service';
import { isFinalOtpStatusError } from '@website/utils/otp';

export const OTP_RESEND_FALLBACK_SECONDS = 30;
export const OTP_DELIVERY_POLL_INTERVAL_MS = 3000;
export const OTP_DELIVERY_POLL_TIMEOUT_MS = 150_000;

export interface SendOtpVariables {
  phoneNumber: string;
  channel: OtpChannel;
}

export function useSendOtp(locale?: string) {
  const [countdown, setCountdown] = useState(0);
  const [sendId, setSendId] = useState<string | null>(null);
  const [lastSent, setLastSent] = useState<(SendOtpVariables & { callAvailable: boolean }) | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCountdown = useCallback((seconds: number) => {
    setCountdown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const mutation = useMutation({
    mutationFn: ({ phoneNumber, channel }: SendOtpVariables) => sendOtpService(phoneNumber, locale, channel),
    onMutate: () => {
      // Never keep polling the previous SMS while a new one is on its way.
      setSendId(null);
    },
    onSuccess: (data, variables) => {
      setSendId(data.sendId ?? null);
      setLastSent({ ...variables, callAvailable: data.callAvailable ?? false });
      startCountdown(data.resendAfterSeconds ?? OTP_RESEND_FALLBACK_SECONDS);
    }
  });

  const { isPending, variables } = mutation;
  /** The channel of the send in flight, else of the last one that went out. */
  const channel: OtpChannel = (isPending ? variables?.channel : lastSent?.channel) ?? 'sms';

  /** True while a code to this number is on its way or was sent less than the resend delay ago. */
  const isCoolingDown = useCallback(
    (phoneNumber: string) =>
      (isPending && variables?.phoneNumber === phoneNumber) || (countdown > 0 && lastSent?.phoneNumber === phoneNumber),
    [isPending, variables, countdown, lastSent]
  );

  return {
    ...mutation,
    countdown,
    canResend: countdown === 0 && !isPending,
    sendId,
    channel,
    lastSent,
    sendError: mutation.error,
    isCoolingDown
  };
}

export const otpSendStatusQueryKey = (sendId: string | null) => ['otp', 'sendStatus', sendId] as const;

const FINAL_DELIVERY_STATUSES = new Set(['delivered', 'failed', 'unknown']);

/**
 * Polls the delivery status of one code (SMS or call) until it is final (delivered, failed,
 * unknown) or {@link OTP_DELIVERY_POLL_TIMEOUT_MS} elapses. `accepted` keeps polling: the
 * handset receipt usually follows within seconds.
 */
export function useOtpDeliveryStatus(sendId: string | null) {
  const [timedOutSendId, setTimedOutSendId] = useState<string | null>(null);

  // A timer, not a render-time clock check: polling has stopped by then, so nothing else
  // would re-render the modal to show that we gave up.
  useEffect(() => {
    if (!sendId) return;
    const timer = setTimeout(() => setTimedOutSendId(sendId), OTP_DELIVERY_POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [sendId]);

  const timedOut = sendId !== null && timedOutSendId === sendId;

  const query = useQuery({
    queryKey: otpSendStatusQueryKey(sendId),
    queryFn: sendId ? ({ signal }) => getOtpSendStatusService(sendId, signal) : skipToken,
    enabled: !timedOut,
    refetchInterval: ({ state }) => {
      if (isFinalOtpStatusError(state.error)) return false;
      return state.data && FINAL_DELIVERY_STATUSES.has(state.data.status) ? false : OTP_DELIVERY_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
    retry: false
  });

  return {
    status: query.data,
    statusUnavailable: timedOut || isFinalOtpStatusError(query.error)
  };
}

export function useVerifyOtp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ phoneNumber, code }: { phoneNumber: string; code: string }) => verifyOtpService(phoneNumber, code),
    onSuccess: async (data) => {
      if (data.verified) {
        await queryClient.invalidateQueries({ queryKey: ['user'] });
      }
    }
  });
}
